import React, { useState, useEffect } from 'react';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import { supabase } from '../supabaseClient';

export default function Presence({ goBack, goHome, navigateTo }) {
  const [showRestrictionModal, setShowRestrictionModal] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setShowRestrictionModal({ message: 'Please log in to start a session.', redirect: 'login' });
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .schema('api')
        .from('user_profiles')
        .select('membership_type, session_complete, session_start_date, created_at, next_membership_start')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError.message);
        setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'login' });
        return;
      }

      console.log('Debug: Profile data:', {
        membership_type: profile.membership_type,
        session_complete: profile.session_complete,
        session_start_date: profile.session_start_date,
        created_at: profile.created_at,
        next_membership_start: profile.next_membership_start
      });

      if (profile.membership_type === 'Free Subscription') {
        const now = new Date();
        console.log('Debug: Current UTC time:', now.toISOString());

        // 24-hour timeout check for incomplete sessions
        if (profile.session_start_date && !profile.session_complete) {
          const sessionStart = new Date(profile.session_start_date + 'Z');
          const hoursSinceStart = (now - sessionStart) / (1000 * 60 * 60);
          console.log('Debug: Session timeout check:', {
            session_start_date: profile.session_start_date,
            parsed_session_start: sessionStart.toISOString(),
            hoursSinceStart: hoursSinceStart.toFixed(2),
            is_timeout: hoursSinceStart > 24
          });
          if (hoursSinceStart > 24) {
            const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
              ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
              : new Date(profile.created_at);
            const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
            const monthsSinceBase = Math.floor(daysSinceBase / 30);
            const resetDate = new Date(baseDate);
            resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);
            setShowRestrictionModal({
              message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
              redirect: 'back'
            });
            return;
          }
        }

        // Monthly session limit check
        const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
          ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
          : new Date(profile.created_at);
        const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
        const monthsSinceBase = Math.floor(daysSinceBase / 30);
        const resetDate = new Date(baseDate);
        resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);

        if (profile.session_complete && daysSinceBase < (monthsSinceBase + 1) * 30) {
          console.log('Debug: Monthly limit reached, reset date:', resetDate.toLocaleDateString());
          setShowRestrictionModal({
            message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
            redirect: 'back'
          });
          return;
        }

        // Update session_start_date for new sessions
        if (!profile.session_complete && !profile.session_start_date) {
          try {
            const { error: updateError } = await supabase
              .schema('api')
              .from('user_profiles')
              .update({
                session_start_date: new Date().toISOString(),
              })
              .eq('id', user.id);

            if (updateError) {
              console.error('Error updating session_start_date:', updateError.message);
              setShowRestrictionModal({ message: 'Failed to start the session. Please try again.', redirect: 'back' });
              return;
            }

            console.log('Session start date updated successfully.');
          } catch (err) {
            console.error('Unexpected error updating session_start_date:', err);
            setShowRestrictionModal({ message: 'An unexpected error occurred while starting the session.', redirect: 'back' });
            return;
          }
        }
      }
    };

    checkSession();
  }, [navigateTo, goBack]);

  const handleSaveProgress = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        alert('User not logged in!');
        return;
      }

      const { error: progressError } = await supabase
        .schema('api')
        .from('user_progress')
        .upsert({
          user_id: user.id,
          current_question_id: null,
          current_component: 'healingFormulasPage/presence',
          animated_helper: 'no', // always set to 'no'
          timestamp: new Date().toISOString(),
        });

      if (progressError) {
        alert('Failed to save progress: ' + progressError.message);
        return;
      }

      alert('Session progress has been saved successfully!');
    } catch (err) {
      alert('An unexpected error occurred while saving progress.');
    }
  };

  // Render modal if restriction or error occurs
  if (showRestrictionModal) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          maxWidth: window.innerWidth <= 600 ? '90vw' : '400px',
          width: '100%',
          backgroundImage: `url(${cardBackgroundImage})`,
          backgroundSize: 'cover',
          border: '2px solid #4B5563',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          fontFamily: 'Times New Roman, Times, serif',
          fontSize: window.innerWidth <= 600 ? '16px' : '18px',
          color: '#4B5563',
        }}>
          <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>{showRestrictionModal.message}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            {showRestrictionModal.redirect === 'back' && (
              <button
                style={{
                  backgroundImage: `url(${buttonImage})`,
                  backgroundSize: 'cover',
                  border: '2px solid black',
                  borderRadius: '10px',
                  padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                  width: window.innerWidth <= 600 ? '45vw' : '140px',
                  fontFamily: 'Times New Roman, Times, serif',
                  fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                  boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  console.log('Debug: Clicking Back button');
                  setShowRestrictionModal(null);
                  goBack();
                }}
              >
                Back
              </button>
            )}
            {(showRestrictionModal.redirect === 'back' || showRestrictionModal.redirect === null) && (
              <button
                style={{
                  backgroundImage: `url(${buttonImage})`,
                  backgroundSize: 'cover',
                  border: '2px solid black',
                  borderRadius: '10px',
                  padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                  width: window.innerWidth <= 600 ? '45vw' : '140px',
                  fontFamily: 'Times New Roman, Times, serif',
                  fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                  boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  console.log('Debug: Clicking My Account button, navigating to myAccount');
                  setShowRestrictionModal(null);
                  navigateTo('myAccount');
                }}
              >
                My Account
              </button>
            )}
            {showRestrictionModal.redirect && showRestrictionModal.redirect !== 'back' && showRestrictionModal.redirect !== null && (
              <button
                style={{
                  backgroundImage: `url(${buttonImage})`,
                  backgroundSize: 'cover',
                  border: '2px solid black',
                  borderRadius: '10px',
                  padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                  width: window.innerWidth <= 600 ? '45vw' : '140px',
                  fontFamily: 'Times New Roman, Times, serif',
                  fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                  boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  console.log('Debug: Clicking OK button, navigating to', showRestrictionModal.redirect);
                  setShowRestrictionModal(null);
                  navigateTo(showRestrictionModal.redirect);
                }}
              >
                OK
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const cardStyle = {
    width: '100%',
    maxWidth: '600px',
    backgroundImage: `url(${cardBackgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '2px solid black',
    borderRadius: '10px',
    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
    marginBottom: '20px',
    padding: '20px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const videoCardStyle = {
    ...cardStyle,
    padding: '20px',
    maxWidth: '600px',
  };

  const nowTextStyle = {
    fontFamily: 'Times New Roman, Times, serif',
    fontSize: 'calc(5vw + 5vh)',
    fontWeight: 'bold',
    color: 'white',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
    textAlign: 'center',
  };

  const secondCardTextStyle = {
    fontFamily: 'Times New Roman, Times, serif',
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'black',
    textAlign: 'center',
    lineHeight: '1.5',
    marginBottom: '16px',
  };

  const titleStyle = {
    fontFamily: 'Times New Roman, Times, serif',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#4B5563',
    textAlign: 'center',
    marginBottom: '16px',
  };

  return (
    <div
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        gap: '20px',
      }}
    >
      <h2
        style={{
          fontFamily: 'Times New Roman, Times, serif',
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#4B5563',
          textAlign: 'center',
          marginTop: '16px',
          marginBottom: '16px',
        }}
      >
        Presence
      </h2>
      {/* Session Helper Cards Row */}
      <div
        className="presence-session-helper-row"
        style={{
          display: 'flex',
          flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
          gap: '16px',
          width: '100%',
          maxWidth: '600px',
          marginBottom: '16px',
        }}
      >
        {/* Left Card */}
        <div style={{
          flex: 1,
          border: '2px solid black',
          borderRadius: '10px',
          padding: '16px',
          textAlign: 'left',
          backgroundImage: `url(${cardBackgroundImage})`,
          backgroundSize: 'cover',
          boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
          marginBottom: window.innerWidth <= 600 ? '16px' : 0,
        }}>
          <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
          <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
            <div>• Now has no thoughts of the past and no worries for the future</div>
            <div>• Now does not wish it was different</div>
            <div>• Now does not blame or analyze</div>
            <div>• Now is Allowing</div>
            <div>• Being present with your feeling should not be rushed</div>
            <div>• When the feeling moves on its own, be sure to click the session complete button at the botton of this page</div>
          </div>
        </div>
        {/* Right Card */}
        <div style={{
          flex: 1,
          border: '2px solid black',
          borderRadius: '10px',
          padding: '16px',
          textAlign: 'left',
          backgroundImage: `url(${cardBackgroundImage})`,
          backgroundSize: 'cover',
          boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
          <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
            <div>1. If I drop the memory of the past, what would be so bad about that?</div>
            <div>2. If I trust that things are happening for me, what does that mean to me?</div>
            <div>3. If I cannot put aside a worry in order to be present, can I be present with the worry fully without resisting it?</div>
            <div>4. Have I been present with the discomfort without wishing it was different or blaming something for it?</div>
            <div>5. Can I bring my awareness to this moment.. and this one?</div>
            <div>6. Have I noticed the power of my presence when I offer it to another person?</div>
          </div>
        </div>
      </div>
      <div style={cardStyle}>
        <span style={nowTextStyle}>NOW</span>
      </div>
      <div style={{ ...cardStyle, height: 'auto' }}>
        <p style={secondCardTextStyle}>
          When we are in presence, we are just Being. We can take action and we can have thoughts while being present, but we are in the perspective of observing those things while they are happening without entering the future or revisiting the past.
        </p>
        <p style={secondCardTextStyle}>
          Sometimes we hear people talk about watching the breath to help us be in the moment. This is because the breath is happening now, and if we watch it, we will be observing Now.
        </p>
        <p style={secondCardTextStyle}>
          Sometimes when we allow ourselves to be present, we can feel overwhelmed by our thoughts or our feelings. We may worry about forgetting something important, or what might happen next. One of the reasons Presence can feel challenging is because we have been programmed and trained into many responses that often play out automatically. When we have been in the habit of focusing another way it can take practice to break the habit.
        </p>
        <p style={secondCardTextStyle}>
          Everything we think for more than 17 seconds, ( according to Abraham Hicks this is the amount of time we have to remove our focus from an unwanted thought) refused to feel, or whatever we decide about ourselves, others, or life is energy and it becomes part of our energy body.  Whenever we have a misunderstanding in our energy; or an unprocessed emotion that we have suppressed, denied, or projected, then the present moment can be flooded with the backlog of what has yet to be resolved within our energy body.
        </p>
        <p style={secondCardTextStyle}>
          The reason for this is because the present moment is where that energy wants to move through. Energy wants to follow its movement through, and presence is the place where it wants to move through. Presence is such a powerful place to be in life. When much of that backlog has been processed, the present moment becomes the place where we can hear our intuition, our inner urges, and curiosities. In this way, the present moment is our doorway to your future and your freedom.
        </p>
        <p style={secondCardTextStyle}>
          When we enter presence and find ourself being faced with unprocessed stuff in the form of worries, fears, or confusion, these are the clues about what is ready to be processed by us next. When we follow the formula for moving any stuck eneryg within us presence is the final step.  Having allowed ourselves to process and be present with the energy dissipates the loop from repeating to get our attention so often. 
        </p>
        <p style={secondCardTextStyle}>
          When we do not have the time to do a self-reflection process as we become aware of these things, there is a to-do list on the home page of this app, as well as a journal to keep track of what we want to explore when we are ready. When we are praticed with presence we become able to offer it to others in our lives.  This simple act of being present with another being can work like magic to open hearts and allow peace.  I found it to be well worth the experience when I offer presence to others. After practicing presence for ourselves, we will find it possible to offer it to others. 
        </p>
      </div>
      <div style={videoCardStyle}>
        <h3 style={titleStyle}>Presence Companion</h3>
        <iframe
          className="video-iframe"
          width="560"
          height="315"
          src="https://www.youtube.com/embed/CaIaDDLU920?si=cjcePCmKizS-9srn"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          style={{ width: '100%', maxWidth: '560px', height: '315px', borderRadius: '8px' }}
        ></iframe>
      </div>
      {/* New Card with Present with self image */}
      <div style={{
        ...cardStyle,
        height: 'auto'
      }}>
        <img
          src="/images/presentwithself.webp" // Fix: use correct case for filename
          alt="Present with self"
          style={{
            width: '100%',
            maxWidth: '360px',
            height: 'auto',
            marginBottom: '12px',
            borderRadius: '8px',
            border: '1px solid #4B5563',
            objectFit: 'contain'
          }}
        />
      </div>
      {/* New Card with Break up meme pg3 image */}
      <div style={{
        ...cardStyle,
        height: 'auto'
      }}>
        <img
          src="/images/breakupmemepg3.webp" // Fix: use correct case for filename
          alt="Break up meme pg3"
          style={{
            width: '100%',
            maxWidth: '360px',
            height: 'auto',
            marginBottom: '12px',
            borderRadius: '8px',
            border: '1px solid #4B5563',
            objectFit: 'contain'
          }}
        />
      </div>
      <div style={{
        ...cardStyle,
        height: 'auto',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18), 0 1.5px 4px rgba(0,0,0,0.15)' // <-- add shadow to card
      }}>
        <h3 style={titleStyle}>After Presence</h3>
        <button
          className="complete-btn"
          style={{
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            border: '2px solid black',
            borderRadius: '10px',
            padding: '10px 20px',
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '300px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.22), 0 1.5px 4px rgba(0,0,0,0.15)' // <-- add shadow to button
          }}
          onClick={() => {
            console.log('Debug: Clicking Session Completion button, navigating to endofSession');
            navigateTo('endofSession');
          }}
        >
          Session Completion
        </button>
      </div>
      <div
        className="presence-btn-row"
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '16px',
          width: '100%',
        }}
      >
        <button
          className="presence-btn"
          style={{
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            border: '2px solid black',
            borderRadius: '10px',
            padding: '10px 20px',
            width: '120px',
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            color: 'white',
          }}
          onClick={() => {
            console.log('Debug: Clicking Back button');
            goBack();
          }}
        >
          Back
        </button>
        <button
          className="presence-btn"
          style={{
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            border: '2px solid black',
            borderRadius: '10px',
            padding: '10px 20px',
            width: '120px',
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            color: 'white',
          }}
          onClick={() => {
            console.log('Debug: Clicking Home button');
            goHome();
          }}
        >
          Home
        </button>
        <button
          className="presence-btn"
          style={{
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            border: '2px solid black',
            borderRadius: '10px',
            padding: '10px 20px',
            width: '120px',
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            color: 'white',
          }}
          onClick={handleSaveProgress}
        >
          Save
        </button>
      </div>
      <style>{`
        @media (max-width: 600px) {
          .presence-btn-row {
            flex-direction: row !important;
            gap: 6px !important;
            width: 100% !important;
            justify-content: space-between !important;
            margin: 0 !important;
            padding: 0 2vw 0 2vw !important;
          }
          .presence-btn-row button {
            width: 32vw !important;
            min-width: 0 !important;
            max-width: 110px !important;
            font-size: 15px !important;
            padding: 10px 0 !important;
            margin: 0 !important;
          }
          .video-iframe {
            width: 90vw !important;
            max-width: 98vw !important;
            height: 50vw !important;
            min-height: 180px !important;
          }
        }
      `}</style>
    </div>
  );
}